import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Nav, NavItem, NavLink } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import usePermission from '../Hooks/usePermission';
import { USER_READ, INVITATION_READ, ROLE_READ, TEAM_READ } from '../constants/permissions';
import InviteUserModal from '../components/InviteUserModal';
import api from '../helpers/api';

import Users from './Users';
import Invitations from './Invitations';
import Roles from './Roles';
import Teams from './Teams';

const UserManagement = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const [inviteModal, setInviteModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [seatUsage, setSeatUsage] = useState(null);

  const visibleTabs = useMemo(() => {
    const config = [
      { key: 'users', label: t('shared.users', 'Users'), icon: 'users', path: '/settings/users', permission: USER_READ, component: Users },
      { key: 'teams', label: t('teams.title', 'Teams'), icon: 'user-check', path: '/settings/teams', permission: TEAM_READ, component: Teams },
      { key: 'invitations', label: t('shared.invitations', 'Invitations'), icon: 'user-plus', path: '/settings/invitations', permission: INVITATION_READ, component: Invitations },
      { key: 'roles', label: t('shared.roles_and_permissions', 'Roles & Permissions'), icon: 'shield', path: '/settings/roles', permission: ROLE_READ, component: Roles },
    ];
    return config.filter(tab => hasPermission(tab.permission));
  }, [hasPermission, t]);

  const activeTab = visibleTabs.find(tab => tab.path === location.pathname);

  const fetchSeatUsage = useCallback(async () => {
    try {
      const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      const orgId = authUser?.activeOrganizationId ?? null;
      if (!orgId) return;

      const { data } = await api.get(`organization-approval/seats?organizationId=${orgId}`);
      setSeatUsage(data.response);
    } catch (error) {
      console.error('Error fetching seat usage:', error);
    }
  }, []);

  useEffect(() => {
    fetchSeatUsage();
  }, [fetchSeatUsage]);

  useEffect(() => {
    if (!activeTab && visibleTabs.length > 0) {
      navigate(visibleTabs[0].path, { replace: true });
    }
  }, [activeTab, visibleTabs, navigate]);

  useEffect(() => {
    document.title = `${t('shared.user_management', 'User Management')} | Arcmedix`;
  }, [location.pathname, t]);

  const handleInviteSuccess = () => {
    setRefreshKey(prev => prev + 1);
    fetchSeatUsage();
  };

  const renderSeatBadge = () => {
    if (!seatUsage) return null;

    const used = seatUsage.used ?? 0;
    const limit = seatUsage.limit;
    const hasLimit = limit != null && limit > 0;
    const percentage = hasLimit ? Math.round((used / limit) * 100) : 0;
    const isNearLimit = hasLimit && percentage >= 80;
    const isAtLimit = hasLimit && used >= limit;

    return (
      <div className="seat-usage-badge ms-3">
        <div className="d-flex align-items-center gap-2">
          <FeatherIcon icon="users" size={14} className="seat-usage-icon" />
          <span className="seat-usage-text">
            <strong>{used}</strong>{hasLimit ? `/${limit}` : ''} {t('shared.seats', 'seats')}{!hasLimit && ` (${t('shared.unlimited', 'unlimited')})`}
          </span>
          {hasLimit && (
            <div className="seat-usage-bar-container">
              <div
                className={`seat-usage-bar ${isAtLimit ? 'at-limit' : isNearLimit ? 'near-limit' : ''}`}
                style={{ width: `${Math.min(percentage, 100)}%` }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!activeTab) return null;

  const ActiveComponent = activeTab.component;

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex align-items-center mb-4 border-bottom">
          <Nav tabs className="user-management-tabs flex-grow-1">
            {visibleTabs.map(tab => (
              <NavItem key={tab.key}>
                <NavLink
                  className={classnames({ active: activeTab.key === tab.key })}
                  onClick={() => navigate(tab.path)}
                  style={{ cursor: 'pointer' }}
                >
                  <FeatherIcon icon={tab.icon} size={16} className="me-2" />
                  {tab.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
          {renderSeatBadge()}
        </div>

        {activeTab.key === 'invitations' ? (
          <Invitations refreshKey={refreshKey} onInvite={() => setInviteModal(true)} />
        ) : activeTab.key === 'users' ? (
          <Users onInvite={() => setInviteModal(true)} />
        ) : activeTab.key === 'teams' ? (
          <Teams />
        ) : (
          <ActiveComponent />
        )}
      </div>

      <InviteUserModal
        isOpen={inviteModal}
        toggle={() => setInviteModal(false)}
        onSuccess={handleInviteSuccess}
      />

      <style>{`
        .user-management-tabs {
          gap: 0.5rem;
        }
        .user-management-tabs .nav-link {
          color: #6c757d;
          background-color: #fff;
        }
        .user-management-tabs .nav-link.active {
          color: #3980c0;
          background-color: #fff;
          border-bottom: 2px solid #3980c0;
        }
        .seat-usage-badge {
          display: inline-flex;
          align-items: center;
          background: #fff;
          border: 1px solid #e0e7ef;
          border-radius: 8px;
          padding: 6px 14px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
        }
        .seat-usage-icon {
          color: #3980c0;
          flex-shrink: 0;
        }
        .seat-usage-text {
          font-size: 0.875rem;
          color: #495057;
          white-space: nowrap;
        }
        .seat-usage-bar-container {
          width: 80px;
          height: 6px;
          background: #e9ecef;
          border-radius: 3px;
          overflow: hidden;
          flex-shrink: 0;
        }
        .seat-usage-bar {
          height: 100%;
          background: #3980c0;
          border-radius: 3px;
          transition: width 0.3s ease;
        }
        .seat-usage-bar.near-limit {
          background: #e8a838;
        }
        .seat-usage-bar.at-limit {
          background: #dc3545;
        }
      `}</style>
    </div>
  );
};

export { UserManagement };
export default UserManagement;
